// MobileAssignTab.jsx — 휴대폰 '배정 관리' 탭 (2026-10-06 UX 4단계)
//   오늘 기준 숫자 → [+ 새 배정] → [진행 중|끝난 배정] → 검색 → 배정 안 된 직원([배정하기]) → 마케터별 접고 펴기
//   → 숨긴 마케터 보이기. 끌어놓기 없이 모두 누르기로 한다(PC와 같은 창·같은 요청).
import React, { useMemo, useState } from 'react';
import { countByToday, periodText, ruleText, statusText, statusTone, todayLabel } from '../assignmentText';
import { retireLabel } from '../UnassignedConsultantPool';
import { MobileMarketerCard } from './MobileMarketerCard';
import { M } from './mobile.styles';

const has = (text, q) => String(text || '').toLowerCase().includes(q);

function EndedList({ assignments, today, keyword }) {
  const q = keyword.trim().toLowerCase();
  const rows = assignments
    .filter((row) => row.end_date)
    .filter((row) => !q || has(row.consultant_name, q) || has(row.marketer_name, q))
    .sort((a, b) => String(b.end_date || '').localeCompare(String(a.end_date || '')));
  if (!rows.length) return <div style={M.empty}>{q ? '검색 결과가 없어요.' : '끝난 배정이 없습니다.'}</div>;
  return (
    <div style={M.card}>
      {rows.map((row, index) => (
        <div key={row.id} style={{ ...M.row, cursor: 'default', borderTop: index ? M.row.borderTop : 'none' }}>
          <span style={{ minWidth: 0 }}>
            <span style={{ ...M.rowName, display: 'block' }}>{row.consultant_name || '-'}</span>
            <span style={{ ...M.rowMeta, display: 'block' }}>마케터 {row.marketer_name || '-'} · {periodText(row)}</span>
            <span style={{ ...M.rowMeta, display: 'block' }}>{ruleText(row)}</span>
          </span>
          <span style={M.badge(statusTone(row, today))}>{statusText(row, today)}</span>
        </div>
      ))}
    </div>
  );
}

export function MobileAssignTab({ vm, board, actions, today }) {
  const [view, setView] = useState('current');
  const [keyword, setKeyword] = useState('');
  const [toggled, setToggled] = useState({}); // 마케터 id → 펼침 여부(사용자가 누른 것만)
  const q = keyword.trim().toLowerCase();
  const counts = countByToday(board.currentAssignments, today);

  const groups = useMemo(() => board.visibleMarketers.map((marketer) => {
    const all = board.currentAssignments.filter((row) => Number(row.marketer_user_id) === marketer.id);
    const nameHit = q && has(marketer.name, q);
    const rows = q && !nameHit ? all.filter((row) => has(row.consultant_name, q)) : all;
    return { marketer, rows, show: !q || nameHit || rows.length > 0 };
  }).filter((group) => group.show), [board.visibleMarketers, board.currentAssignments, q]);

  const unassigned = board.unassignedConsultants.filter((user) => !q || has(user.name, q) || has(user.department, q));
  const isOpen = (group) => (q ? true : toggled[group.marketer.id] ?? group.rows.length > 0);
  const toggle = (group) => setToggled((prev) => ({ ...prev, [group.marketer.id]: !isOpen(group) }));

  if (vm.loading) return <div style={M.empty}>불러오는 중입니다.</div>;
  if (!board.allMarketers.length) return <div style={M.empty}>마케터가 없습니다.</div>;

  return (
    <div style={M.page}>
      <div style={M.stats}>
        <span>오늘({todayLabel(today)}) 기준</span>
        <span style={M.stat('ok')}>근무 중 {counts.active}</span>
        <span style={M.stat('blue')}>시작 예정 {counts.upcoming}</span>
        {counts.ended > 0 && <span style={M.stat('muted')}>이번 달 끝남 {counts.ended}</span>}
        <span style={M.stat(board.unassignedConsultants.length ? 'warn' : 'ok')}>배정 안 된 직원 {board.unassignedConsultants.length}</span>
      </div>

      <button type="button" style={M.primaryFull} onClick={() => vm.openAdd(null)}>+ 새 배정</button>

      <div style={M.seg} role="tablist" aria-label="배정 보기">
        <button type="button" role="tab" aria-selected={view === 'current'} style={M.segBtn(view === 'current')} onClick={() => setView('current')}>진행 중</button>
        <button type="button" role="tab" aria-selected={view === 'ended'} style={M.segBtn(view === 'ended')} onClick={() => setView('ended')}>끝난 배정</button>
      </div>

      <input
        type="search"
        style={M.search}
        value={keyword}
        onChange={(event) => setKeyword(event.target.value)}
        placeholder="마케터·직원 이름 검색"
        aria-label="마케터·직원 이름 검색"
      />

      {view === 'ended' ? (
        <EndedList assignments={vm.assignments} today={today} keyword={keyword} />
      ) : (
        <>
          {unassigned.length > 0 && (
            <div style={{ ...M.card, borderColor: 'rgba(245,158,11,0.45)' }}>
              <div style={{ padding: '12px 14px', background: 'rgba(245,158,11,0.06)' }}>
                <div style={M.sectionTitle}>배정 안 된 직원 {unassigned.length}명</div>
                <div style={M.sectionHint}>이번 달에 1:1 배정이 없는 행사지원 직원이에요. [배정하기]를 누르면 마케터를 고를 수 있어요.</div>
              </div>
              {unassigned.map((user) => (
                <div key={user.id} style={{ ...M.row, cursor: 'default' }}>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ ...M.rowName, display: 'block' }}>{user.name || '-'}</span>
                    <span style={{ ...M.rowMeta, display: 'block' }}>{user.department || '부서 없음'}{retireLabel(user) ? ` · ${retireLabel(user)}` : ''}</span>
                  </span>
                  <button type="button" style={M.footBtn('blue')} onClick={() => actions.requestAssign(user)} disabled={vm.saving}>배정하기</button>
                </div>
              ))}
            </div>
          )}

          {groups.length === 0 && <div style={M.empty}>{q ? '검색 결과가 없어요.' : '보이는 마케터가 없어요.'}</div>}
          {groups.map((group) => (
            <MobileMarketerCard
              key={group.marketer.id}
              marketer={group.marketer}
              rows={group.rows}
              today={today}
              open={isOpen(group)}
              onToggle={() => toggle(group)}
              onOpenRow={actions.openDetail}
              onAdd={vm.openAdd}
              onHide={board.toggleHide}
            />
          ))}

          {board.hiddenMarketers.length > 0 && (
            <div style={M.hiddenBox}>
              <span style={M.hiddenLabel}>숨긴 마케터</span>
              {board.hiddenMarketers.map((marketer) => (
                <button key={marketer.id} type="button" style={M.chipBtn} onClick={() => board.toggleHide(marketer.id)}>
                  {marketer.name} 보이기
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
